<script setup lang="ts">
const {bp} = useViewport()
const {t} = useI18n({
  useScope: 'global'
})


interface WithSidebarProps {
  sidebarTitle: string;
  sidebarIcon?: string;
}

const { sidebarTitle = 'Filters', sidebarIcon = 'filter' } = defineProps<WithSidebarProps>();

const isSidebarActive = ref(false);
const isMobile = bp.smaller('laptopS')

const toggleSidebar = () => {
  isSidebarActive.value = !isSidebarActive.value;
}

</script>

<template>
  <div class="layout-with-sidebar">
    <div class="container">
      <slot name="before-wrapper" />
      <FormKit
        v-if="isMobile"
        outer-class="filter"
        variant="outline"
        type="button"
        color="gradient"
        width="full"
        :label="sidebarTitle"
        :prefix-icon="sidebarIcon"
        @click="toggleSidebar()"
      />
      <div class="content-wrapper">
        <div class="sidebar-column">
        <AppCard
          class="sidebar"
          :class="{ active: isSidebarActive }"
        >
          <slot
            name="mobile-header"
          >
            <div
              v-if="isMobile"
              class="sidebar__header"
            >
              <div class="sidebar__header-label">
                <FormKit
                  type="submit"
                  variant="icon"
                  prefix-icon="filter"
                />
                <label class="label">{{ t('cars.filters.title') }}</label>
              </div>
              <FormKit
                type="submit"
                variant="icon"
                prefix-icon="cross"
                @click="toggleSidebar()"
              />
            </div>
          </slot>
          <slot
            name="header"
          >
            <div
              v-if="!isMobile"
              class="sidebar__header"
            >
              <div class="sidebar__header-label">
                <label class="label">{{ sidebarTitle }}</label>
              </div>
            </div>
          </slot>


          <div class="sidebar__content">
            <slot name="sidebar" />
          </div>
        </AppCard>
        <div
          v-if="$slots['after-sidebar']"
          class="after-sidebar"
        >
          <slot name="after-sidebar" />
        </div>
        </div>

        <div class="content">
          <slot />
        </div>
      </div>
      <slot name="after-wrapper" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-label {
  display: block;
  font-weight: 700;
  margin-bottom: 20px;
}

.layout-with-sidebar {
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  flex: 1;

  .container {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
}

.content-wrapper {
  display: grid;
  grid-template-columns: 237px 1fr;
  align-items: start;
  gap: 15px;

  @media only screen and (max-width: $laptopS) {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .sidebar-column {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .sidebar {
    position: relative;
    width: 237px;
    grid-column: 1;
    grid-row: 1;
    height: fit-content;
    padding: 15px;
    margin: 0;

    &.active {
      opacity: 1;
      visibility: visible;
      transform: none;
    }

    @media only screen and (max-width: $laptopS) {
      position: fixed;
      z-index: 8;
      left: 0;
      top: 50px;
      right: 0;
      bottom: 0;
      width: unset;
      height: unset;
      background: #fff;
      overflow: auto;

      opacity: 0;
      visibility: hidden;
      transform: translateX(-100%);
      transition: opacity .3s, visibility .3s, transform .3s;
    }

    &::before,
    &::after {
      position: absolute;
      z-index: -2;
      content: "";
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      border-radius: 10px;
      background: linear-gradient(45deg, var(--primary-color, #2D2D86) 0%, rgba(240, 73, 79, 1) 100%);

      @media only screen and (max-width: $laptopS) {
        display: none;
      }
    }

    &::after {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #fff;
      z-index: -1;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      margin-bottom: 20px;

      @media only screen and (max-width: $laptopS) {
        margin-bottom: 10px;
      }

      .icon {
        display: none;
        @media only screen and (max-width: $laptopS) {
          display: block;
        }
      }

      &-label {
        display: flex;
        align-items: center;

        .label {
          font-weight: 700;
          margin-bottom: 3px;
        }
      }
    }


    .ps {
      max-height: calc(100% - 39px);
      padding-right: 20px;
    }

    .item-label {
      display: block;
      font-size: 12px;
      margin-bottom: 8px;
    }
  }

  .after-sidebar {
    min-width: 0;
    :deep(.card) {
      margin: 0;
    }
  }

  .content {
    min-width: 0;
  }
}

.filter {
  margin-bottom: 20px;

  :deep(.indigo-ui-input) {
    font-weight: 700;
  }
}


.sidebar__content {
  :deep(.indigo-ui-outer), :deep(.indigo-ui-wrapper) {
    margin-bottom: 15px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
